import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { CustomerToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '~/components/system/form-field';
import { CustomerPicker } from '~/components/order/customer-picker';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useAdjustLoyaltyPoints } from '~/hooks/use-loyalty-customers';
import { dayEndIso, toDayInput } from '~/lib/loyalty-dates';
import { ADJUSTMENT_NOTE_MAX, loyaltyAdjustmentSchema, type LoyaltyAdjustmentFormValues } from '~/lib/schemas/loyalty-adjustment';

/** The grid row the adjustment is for; null: the admin picks the customer (one with no points is not in the grid). */
export interface AdjustTarget {
	cid: number;
	name: string;
	points: number;
}

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	target: AdjustTarget | null;
}

/**
 * Adds or takes away a customer's points by hand, with a required reason (admin-only). Points added pay off a debt
 * first and expire on the date given, or as the expiry setting says; points taken away beyond the balance become a debt.
 */
export function LoyaltyAdjustDialog({ open, onOpenChange, target }: Props) {
	const { t } = useTranslation();
	const adjust = useAdjustLoyaltyPoints();
	const [picked, setPicked] = useState<CustomerToken | null>(null);
	const form = useForm<LoyaltyAdjustmentFormValues>({
		resolver: zodResolver(loyaltyAdjustmentSchema) as Resolver<LoyaltyAdjustmentFormValues>,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const points = watch('pnt');
	const exdt = watch('exdt');
	const note = watch('nt') ?? '';

	useEffect(() => {
		if (!open) return;
		setPicked(null);
		reset({ cid: target?.cid, pnt: undefined, nt: '', exdt: null });
	}, [open, target, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			const row = await adjust.mutateAsync(values);
			toast.success(t('loyalty.customers.adjust.saved', { points: row.pnt ?? 0 }));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t('loyalty.customers.adjust.title')}</DialogTitle>
					<DialogDescription>
						{target
							? t('loyalty.customers.adjust.subtitle', { name: target.name, points: target.points })
							: t('loyalty.customers.adjust.pickCustomer')}
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					{!target && (
						<FormField label={t('loyalty.customers.adjust.customer')} error={formState.errors.cid?.message}>
							<CustomerPicker
								selected={picked}
								onSelect={c => {
									setPicked(c);
									setValue('cid', c.id as number, { shouldValidate: true });
								}}
								onClear={() => {
									setPicked(null);
									setValue('cid', undefined as unknown as number, { shouldValidate: true });
								}}
							/>
						</FormField>
					)}
					<FormField label={t('loyalty.customers.adjust.points')} error={formState.errors.pnt?.message}>
						<Input
							type="number"
							step={1}
							autoFocus={Boolean(target)}
							{...register('pnt', { setValueAs: v => (v === '' || v == null ? undefined : Number(v)) })}
							aria-invalid={Boolean(formState.errors.pnt)}
						/>
						<span className="text-xs text-foreground-muted">{t('loyalty.customers.adjust.pointsHint')}</span>
					</FormField>
					<FormField label={t('loyalty.customers.adjust.note')} error={formState.errors.nt?.message}>
						<Textarea {...register('nt')} maxLength={ADJUSTMENT_NOTE_MAX} rows={2} aria-invalid={Boolean(formState.errors.nt)} />
						<span className="text-right text-xs text-foreground-muted tabular-nums">
							{note.length}/{ADJUSTMENT_NOTE_MAX}
						</span>
					</FormField>
					{points > 0 && (
						<FormField label={t('loyalty.customers.adjust.expiry')} error={formState.errors.exdt?.message}>
							<Input
								type="date"
								value={toDayInput(exdt)}
								onChange={e => setValue('exdt', dayEndIso(e.target.value), { shouldDirty: true, shouldValidate: true })}
							/>
							<span className="text-xs text-foreground-muted">{t('loyalty.customers.adjust.expiryHint')}</span>
						</FormField>
					)}
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={adjust.isPending}>
							{adjust.isPending ? t('common.saving') : t('loyalty.customers.adjust.submit')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
